import React, { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { playerPosition } from "./controls";
import { playHorseSound } from "./audio";

interface HorseProps {
  position: [number, number, number];
  rotationY?: number;
  color?: string;
}

const SOUND_RADIUS = 9;
const WANDER_RADIUS = 2.6;

/** A grazing horse — slowly wanders a small patch, dipping its head to graze. */
const Horse: React.FC<HorseProps> = ({ position, rotationY = 0, color = "#6b4a30" }) => {
  const groupRef = useRef<THREE.Group>(null);
  const neckRef = useRef<THREE.Group>(null);
  const soundTimer = useRef(2 + Math.random() * 5);
  const grazeOffset = useRef(Math.random() * 10);
  const wanderPhase = useRef(rotationY + Math.random() * Math.PI * 2);
  const wanderSpeed = useRef(0.06 + Math.random() * 0.05);
  const worldPos = useRef(new THREE.Vector3(...position));

  useFrame(({ clock }, delta) => {
    const t = clock.getElapsedTime() + grazeOffset.current;
    // Head-down graze cycle — quick dip, brief hold, quick rise
    const graze = Math.max(0, Math.sin(t * 0.9)) ** 0.6;
    if (neckRef.current) neckRef.current.rotation.x = graze * 1.1;

    const wt = clock.getElapsedTime() * wanderSpeed.current + wanderPhase.current;
    const x = position[0] + Math.sin(wt) * WANDER_RADIUS;
    const z = position[2] + Math.cos(wt * 0.8) * WANDER_RADIUS * 0.7;
    if (groupRef.current) {
      groupRef.current.position.set(x, position[1], z);
      const dx = Math.cos(wt) * WANDER_RADIUS;
      const dz = -Math.sin(wt * 0.8) * WANDER_RADIUS * 0.7 * 0.8;
      groupRef.current.rotation.y = Math.atan2(-dx, -dz);
      worldPos.current.set(x, position[1], z);
    }

    soundTimer.current -= delta;
    if (soundTimer.current <= 0) {
      soundTimer.current = 6 + Math.random() * 8;
      if (playerPosition.distanceTo(worldPos.current) < SOUND_RADIUS) playHorseSound();
    }
  });

  return (
    <group ref={groupRef} position={position} rotation={[0, rotationY, 0]} scale={0.85}>
      {/* Body */}
      <mesh position={[0, 1.05, 0]} castShadow>
        <boxGeometry args={[0.62, 0.6, 1.3]} />
        <meshStandardMaterial color={color} roughness={0.95} />
      </mesh>
      {/* Neck + head (pivots to graze) */}
      <group ref={neckRef} position={[0, 1.35, -0.68]}>
        <mesh position={[0, 0.25, -0.1]} castShadow>
          <boxGeometry args={[0.32, 0.55, 0.32]} />
          <meshStandardMaterial color={color} roughness={0.95} />
        </mesh>
        <mesh position={[0, 0.52, -0.32]} castShadow>
          <boxGeometry args={[0.26, 0.28, 0.42]} />
          <meshStandardMaterial color={color} roughness={0.95} />
        </mesh>
      </group>
      {/* Legs */}
      {[
        [-0.22, 0.45, 0.45],
        [0.22, 0.45, 0.45],
        [-0.22, 0.45, -0.45],
        [0.22, 0.45, -0.45],
      ].map((p, i) => (
        <mesh key={i} position={p as [number, number, number]} castShadow>
          <boxGeometry args={[0.16, 0.9, 0.16]} />
          <meshStandardMaterial color="#2c1e14" roughness={0.95} />
        </mesh>
      ))}
      {/* Tail */}
      <mesh position={[0, 1.0, 0.68]} rotation={[0.3, 0, 0]}>
        <boxGeometry args={[0.1, 0.5, 0.1]} />
        <meshStandardMaterial color="#2c1e14" roughness={0.95} />
      </mesh>
      {/* Mane */}
      <mesh position={[0, 1.55, -0.55]}>
        <boxGeometry args={[0.1, 0.3, 0.4]} />
        <meshStandardMaterial color="#2c1e14" roughness={0.95} />
      </mesh>
    </group>
  );
};

export default Horse;
